// src/pages/TermsOfServicePage.jsx
import React from 'react';
import { Container, Typography, Box, Paper, List, ListItem, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const TermsOfServicePage = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Box sx={{ py: 8, minHeight: '100vh' }}>
      <Container maxWidth="md">
        <Paper 
          elevation={3}
          sx={{
            p: 4,
            background: isDarkMode 
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'none',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`
          }}
        >
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, color: '#5980a6' }}>
            Terms of Service
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary' }}>
            Last updated: {new Date().toLocaleDateString()}
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph>
              By accessing and using XAUPilot ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              2. Service Description
            </Typography>
            <Typography variant="body1" paragraph>
              XAUPilot provides trading signals, market analysis, and educational content related to precious metals trading, particularly gold (XAU). Our services include:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Real-time and delayed trading signals" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Market analysis and commentary" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Educational content and newsletters" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Performance metrics and historical data" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              3. User Responsibilities
            </Typography>
            <Typography variant="body1" paragraph>
              You agree to:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Provide accurate and complete registration information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Maintain the security of your account credentials" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Use the service in compliance with all applicable laws" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Not share your account access with others" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Notify us immediately of any unauthorized use of your account" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              4. Trading Risks and Disclaimers
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontWeight: 600, color: '#cf6f61' }}>
              IMPORTANT: Trading involves substantial risk of loss and is not suitable for all investors.
            </Typography>
            <Typography variant="body1" paragraph>
              XAUPilot provides information and signals for educational and informational purposes only. We do not provide investment advice, and our signals should not be construed as recommendations to buy or sell any financial instruments. You should:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Conduct your own research before making trading decisions" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Consider your financial situation and risk tolerance" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Consult with a qualified financial advisor if needed" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Never trade with money you cannot afford to lose" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              5. Subscription and Payment Terms
            </Typography>
            <Typography variant="body1" paragraph>
              Our service offers both free and paid subscription tiers:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Free Plan: Includes delayed signals and basic features"
                  secondary="No payment required, limited functionality"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Standard Plan: $29/month or $300/annually"
                  secondary="Real-time signals, premium features, priority support"
                />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph>
              Paid subscriptions automatically renew unless cancelled. You may cancel at any time through your account settings. Refunds are provided according to our refund policy outlined in Section 8.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              6. Intellectual Property
            </Typography>
            <Typography variant="body1" paragraph>
              All content, including but not limited to text, graphics, logos, trading signals, analysis, and software, is the property of XAUPilot and is protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              7. Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, XAUPILOT SHALL NOT BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE, INCLUDING BUT NOT LIMITED TO TRADING LOSSES, LOST PROFITS, OR DATA LOSS.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              8. Refund Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We offer a 7-day money-back guarantee for new subscribers to our Standard plan. Refund requests must be submitted within 7 days of initial subscription. Annual subscriptions are prorated for refunds after the initial 7-day period.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              9. Termination
            </Typography>
            <Typography variant="body1" paragraph>
              We reserve the right to suspend or terminate your account at our sole discretion if you violate these terms or engage in prohibited activities. Upon termination, your access to the service will cease immediately.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              10. Changes to Terms
            </Typography>
            <Typography variant="body1" paragraph>
              We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Your continued use of the service constitutes acceptance of any changes.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              11. Contact Information
            </Typography>
            <Typography variant="body1" paragraph>
              For questions about these Terms of Service, please contact us at:
              <br />
              Email: support@XAUPilot.com
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfServicePage;



